import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Space, Switch, Typography, message, Alert } from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { useAuth } from "../../../hooks";
import { LiveGamesApi } from "../../../api/liveGames";
import {
  LiveGamesTable,
  LiveGameCreateWizard,
  LiveGameEditModal,
  DeleteConfirmDialog,
  SendSdkModal,
} from "../../../components/Admin/LiveGamesCmp";

const { Title, Paragraph } = Typography;

const api = new LiveGamesApi();

/**
 * Pagina admin de gestion del catalogo LiveStreamGame (M13 F2.7).
 *
 * Vive en /admin/livegames. Solo role=admin (backend rechaza con 403 al
 * resto, pero tambien filtramos en menu).
 *
 * Flujo:
 *   - lista paginada con tabla + acciones inline.
 *   - boton primary "Nuevo LiveGame" -> wizard 3 pasos.
 *   - acciones por fila: edit, toggle, delete.
 */
export function Page_LiveGames() {
  const { accessToken } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [includeDisabled, setIncludeDisabled] = useState(true);

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editGame, setEditGame] = useState(null);
  const [deleteGame, setDeleteGame] = useState(null);
  const [sendSdkGame, setSendSdkGame] = useState(null);

  const reload = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const list = await api.list(accessToken, includeDisabled);
      setItems(list);
    } catch (e) {
      setError(e?.message || "Error cargando juegos");
    } finally {
      setLoading(false);
    }
  }, [accessToken, includeDisabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleCreate = useCallback(
    async (payload) => {
      const game = await api.create(accessToken, payload);
      reload();
      return game;
    },
    [accessToken, reload],
  );

  const handleSave = useCallback(
    async (id, patch) => {
      const updated = await api.update(accessToken, id, patch);
      reload();
      return updated;
    },
    [accessToken, reload],
  );

  const handleToggle = useCallback(
    async (game) => {
      try {
        await api.toggle(accessToken, game.id);
        reload();
      } catch (e) {
        message.error(e?.message || "Error toggling");
      }
    },
    [accessToken, reload],
  );

  const handleDeleteConfirm = useCallback(
    async ({ mode }) => {
      if (!deleteGame) return;
      const hard = mode === "hard";
      try {
        await api.remove(accessToken, deleteGame.id, hard);
        message.success(
          hard ? "Juego eliminado definitivamente" : "Juego desactivado",
        );
        reload();
      } catch (e) {
        if (e?.sessionCount !== undefined) {
          message.error(
            `No se puede borrar: hay ${e.sessionCount} sesiones historicas`,
          );
        } else {
          message.error(e?.message || "Error eliminando");
        }
        throw e;
      }
    },
    [accessToken, deleteGame, reload],
  );

  const uploadIcon = useCallback(
    async (gameKey, file) => api.uploadIconFile(accessToken, gameKey, file),
    [accessToken],
  );

  const sessionCountFetcher = useCallback(
    (id) => api.sessionCount(accessToken, id),
    [accessToken],
  );

  const handleSendSdk = useCallback(
    async (email, customMessage) => {
      if (!sendSdkGame) return null;
      return api.sendSdk(accessToken, sendSdkGame.id, email, customMessage);
    },
    [accessToken, sendSdkGame],
  );

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: "0 auto" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 16,
        }}
      >
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>
            Live Games
          </Title>
          <Paragraph type="secondary">
            Catalogo de juegos autorizados para usar el SDK Educa360 Live. Cada
            juego tiene un gameKey unico que el dev mete en su LiveStreamConfig
            de Unity.
          </Paragraph>
        </div>
        <Space>
          <Switch
            checked={includeDisabled}
            onChange={setIncludeDisabled}
            checkedChildren="Todos"
            unCheckedChildren="Solo activos"
          />
          <Button icon={<ReloadOutlined />} onClick={reload} loading={loading}>
            Recargar
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setWizardOpen(true)}
          >
            Nuevo LiveGame
          </Button>
        </Space>
      </div>

      {error && (
        <Alert
          type="error"
          message="Error cargando juegos"
          description={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card bodyStyle={{ padding: 0 }}>
        <LiveGamesTable
          items={items}
          loading={loading}
          onEdit={setEditGame}
          onDelete={setDeleteGame}
          onToggle={handleToggle}
          onSendSdk={setSendSdkGame}
        />
      </Card>

      <LiveGameCreateWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onCreate={handleCreate}
        onUploadIcon={uploadIcon}
        onUpdateGame={handleSave}
      />

      <LiveGameEditModal
        open={!!editGame}
        game={editGame}
        onClose={() => setEditGame(null)}
        onSave={(patch) => handleSave(editGame.id, patch)}
        onUploadIcon={uploadIcon}
      />

      <DeleteConfirmDialog
        open={!!deleteGame}
        game={deleteGame}
        onClose={() => setDeleteGame(null)}
        onConfirm={handleDeleteConfirm}
        sessionCountFetcher={sessionCountFetcher}
      />

      <SendSdkModal
        open={!!sendSdkGame}
        game={sendSdkGame}
        onClose={() => setSendSdkGame(null)}
        onSend={handleSendSdk}
      />
    </div>
  );
}
